import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Stack,
    Typography,
} from "@mui/material";
import {BlobProvider} from "@react-pdf/renderer";
import type {ReactElement} from "react";
import FeePdf from "@/components/Fees/FeePdf.tsx";
import {buildFeeLink, type LinkableFee} from "@/Helpers/buildFeeLink.ts";
import {useFeeQrCode} from "@/hooks/useFeeQrCode.ts";

type Props = {
    fee: (LinkableFee & {name: string}) | null;
    onClose: () => void;
};

const FeeQrDialog = ({fee, onClose}: Props): ReactElement => {
    const feeLink = buildFeeLink(fee ?? undefined, window.origin);
    const {qrCode, failed} = useFeeQrCode(feeLink);

    const feeDetails = feeLink && (
        <>
            {fee?.shortCode && <Typography>Fee code {fee.shortCode}</Typography>}
            <Link href={feeLink} target="_blank">
                <Typography>{feeLink}</Typography>
            </Link>
        </>
    );

    const body = () => {
        if (!feeLink) {
            return <Typography>This fee does not have a shareable link.</Typography>;
        }

        if (failed) {
            return (
                <Stack sx={{alignItems: "center", gap: 2}}>
                    <Alert severity="warning">
                        The QR code could not be generated. The link below still works.
                    </Alert>
                    {feeDetails}
                </Stack>
            );
        }

        if (!qrCode) {
            return (
                <Stack sx={{alignItems: "center"}}>
                    <CircularProgress />
                </Stack>
            );
        }

        return (
            <Stack sx={{alignItems: "center", gap: 2}}>
                <img src={qrCode} alt={`QR code for ${fee?.name}`} width={220} />
                {feeDetails}
                <BlobProvider
                    document={
                        <FeePdf feeLink={feeLink} feeName={fee?.name ?? ""} qrCode={qrCode} />
                    }
                >
                    {({url}) =>
                        url ? (
                            <Link href={url} target="_blank">
                                <Typography>Download QR Code &amp; Link</Typography>
                            </Link>
                        ) : (
                            <CircularProgress size={20} />
                        )
                    }
                </BlobProvider>
            </Stack>
        );
    };

    return (
        <Dialog open={Boolean(fee)} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{fee?.name}</DialogTitle>
            <DialogContent>{body()}</DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeeQrDialog;
